<div class="tenant-view-modal">
  <!-- Modal Header -->
  <div class="modal-header">
    <h2 mat-dialog-title>{{ dialogTitle }}</h2>
    <button mat-icon-button (click)="onClose()" class="close-button">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Tab Navigation -->
  <mat-tab-group [(selectedIndex)]="selectedTabIndex" class="tenant-tabs">
    
    <!-- Tab 1: Details and Change -->
    <mat-tab label="Details & Change">
      <div class="tab-content details-tab">
        <!-- Edit Mode Toggle -->
        <div class="tab-header">
          <div class="tab-title">
            <mat-icon>business</mat-icon>
            <h3>Tenant Information</h3>
          </div>
          <div class="tab-actions">
            @if (!isEditing) {
              <button mat-raised-button color="primary" (click)="toggleEditMode()">
                <mat-icon>edit</mat-icon>
                Edit Details
              </button>
            } @else {
              <button mat-button (click)="onCancel()" class="cancel-btn">
                <mat-icon>close</mat-icon>
                Cancel
              </button>
              <button mat-raised-button color="primary" (click)="onSave()" [disabled]="!tenantForm.valid || isLoading">
                <mat-icon>save</mat-icon>
                Save Changes
              </button>
            }
          </div>
        </div>

        <!-- Tenant Form -->
        <form [formGroup]="tenantForm" class="tenant-details-form">
          <!-- Basic Information -->
          <div class="form-section">
            <h4 class="section-title">Basic Information</h4>
            <div class="form-row">
              <mat-form-field appearance="outline" class="half-width">
                <mat-label>Tenant Name</mat-label>
                <input matInput formControlName="name" placeholder="Enter tenant name">
                @if (tenantForm.get('name')?.hasError('required')) {
                  <mat-error>Tenant name is required</mat-error>
                }
              </mat-form-field>
              
              <mat-form-field appearance="outline" class="half-width">
                <mat-label>Slug</mat-label>
                <input matInput formControlName="slug" placeholder="Enter tenant slug">
                @if (tenantForm.get('slug')?.hasError('required')) {
                  <mat-error>Tenant slug is required</mat-error>
                }
              </mat-form-field>
            </div>

            <div class="form-row">
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Description</mat-label>
                <textarea matInput formControlName="description" placeholder="Enter tenant description" rows="3"></textarea>
              </mat-form-field>
            </div>
          </div>

          <!-- Contact Information -->
          <div class="form-section">
            <h4 class="section-title">Contact Information</h4>
            <div class="form-row">
              <mat-form-field appearance="outline" class="half-width">
                <mat-label>Contact Email</mat-label>
                <input matInput formControlName="contactEmail" type="email" placeholder="Enter email">
                @if (tenantForm.get('contactEmail')?.hasError('email')) {
                  <mat-error>Please enter a valid email</mat-error>
                }
              </mat-form-field>
              
              <mat-form-field appearance="outline" class="half-width">
                <mat-label>Contact Phone</mat-label>
                <input matInput
                       type="tel"
                       formControlName="contactPhone"
                       placeholder="+1 234 567 8900"
                       (keydown)="onPhoneInput($event)"
                       (paste)="onPhoneInput($event)">
              </mat-form-field>
            </div>

            <div class="form-row">
              <mat-form-field appearance="outline" class="half-width">
                <mat-label>City</mat-label>
                <input matInput formControlName="city" placeholder="Enter city">
              </mat-form-field>
              
              <mat-form-field appearance="outline" class="half-width">
                <mat-label>Country</mat-label>
                <input matInput formControlName="country" placeholder="Enter country">
              </mat-form-field>
            </div>

            <div class="form-row">
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Website</mat-label>
                <input matInput formControlName="website" placeholder="Enter website URL">
              </mat-form-field>
            </div>
          </div>
        </form>
      </div>
    </mat-tab>

    <!-- Tab 2: Documents -->
    <mat-tab label="Documents">
      <div class="tab-content documents-tab">
        <!-- Documents Header -->
        <div class="tab-header">
          <div class="tab-title">
            <mat-icon>folder</mat-icon>
            <h3>Documents & Files</h3>
          </div>
          <div class="tab-actions">
            <button mat-raised-button color="primary" (click)="uploadDocument()">
              <mat-icon>upload</mat-icon>
              Upload Document
            </button>
          </div>
        </div>

        <!-- Documents List -->
        <div class="documents-section">
          @if (loadingDocuments) {
            <div class="loading-documents">
              <mat-spinner diameter="40"></mat-spinner>
              <p>Loading documents...</p>
            </div>
          } @else if (documents.length > 0) {
            <div class="documents-grid">
              @for (document of documents; track document.id) {
                <mat-card class="document-card">
                  <mat-card-header>
                    <div mat-card-avatar class="document-icon">
                      <mat-icon>{{ getDocumentIcon(document.type) }}</mat-icon>
                    </div>
                    <mat-card-title>{{ document.name }}</mat-card-title>
                    <mat-card-subtitle>{{ document.type }}</mat-card-subtitle>
                  </mat-card-header>
                  
                  <mat-card-content>
                    <div class="document-meta">
                      <div class="meta-item">
                        <mat-icon>folder_open</mat-icon>
                        <span>{{ formatFileSize(document.size) }}</span>
                      </div>
                      <div class="meta-item">
                        <mat-icon>schedule</mat-icon>
                        <span>{{ formatDate(document.createdAt) }}</span>
                      </div>
                      <div class="meta-item">
                        <mat-icon>person</mat-icon>
                        <span>{{ document.uploadedBy }}</span>
                      </div>
                    </div>
                  </mat-card-content>
                  
                  <mat-card-actions align="end">
                    <button mat-icon-button (click)="downloadDocument(document)" matTooltip="Download" class="action-btn download-btn">
                      <mat-icon>download</mat-icon>
                    </button>
                    <button mat-icon-button (click)="deleteDocument(document)" matTooltip="Delete" class="action-btn delete-btn">
                      <mat-icon>delete</mat-icon>
                    </button>
                  </mat-card-actions>
                </mat-card>
              }
            </div>
          } @else {
            <div class="empty-documents">
              <mat-icon class="empty-icon">folder_open</mat-icon>
              <h3>No Documents</h3>
              <p>No documents have been uploaded for this tenant yet.</p>
              <button mat-raised-button color="primary" (click)="uploadDocument()">
                <mat-icon>upload</mat-icon>
                Upload First Document
              </button>
            </div>
          }
        </div>
      </div>
    </mat-tab>
  </mat-tab-group>
</div>
